import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
} from "@monbits/ui";
import type * as React from "react";
import { useForm } from "react-hook-form";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import type { OrderParams } from "src/types";
import { BLOCKCHAIN_TICKER, CURRENCY } from "src/types";
import * as z from "zod";

interface OrderCreateFormProps {
	onFinish: (values: OrderParams) => void;
	onFinishFailed: () => void;
	isSubmitting: boolean;
}

// toleranceMode distinguishes the three merchant-facing choices for the per-order
// underpayment tolerance. "account" sends nothing (falls through to account defaults);
// "shortfall" reveals a percent input (sent as-is); "exact" sends 0.
const TOLERANCE_MODES = ["account", "shortfall", "exact"] as const;

// formatPreviewUsd renders the live-computed USD tolerance for the form helper line.
// Values below one cent render as "< $0.01" so small percents on small prices stay
// honest instead of rounding to $0.00 or $0.01.
const formatPreviewUsd = (value: number): string => {
	if (value > 0 && value < 0.01) {
		return "< $0.01";
	}
	return `$${value.toFixed(2)}`;
};

const formSchema = z
	.object({
		merchantOrderId: z
			.string()
			.min(1, "Order ID is required")
			.max(255, "Order ID must not exceed 255 characters"),
		currency: z.string().min(1, "Please select a currency"),
		price: z.number().min(0.01, "Price must be at least 0.01"),
		cryptoCurrency: z.string().min(1, "Please select a crypto currency"),
		description: z
			.string()
			.max(500, "Description must not exceed 500 characters")
			.optional(),
		toleranceMode: z.enum(TOLERANCE_MODES),
		tolerancePercent: z
			.number()
			.min(0, "Tolerance must be 0 or greater")
			.max(100, "Tolerance must be 100 or less")
			.optional(),
	})
	.refine(
		(values) =>
			values.toleranceMode !== "shortfall" ||
			values.tolerancePercent !== undefined,
		{
			path: ["tolerancePercent"],
			message: "Enter the percent you want to accept",
		},
	);

type FormValues = z.infer<typeof formSchema>;

const OrderCreateForm: React.FC<OrderCreateFormProps> = ({
	onFinish,
	onFinishFailed,
}) => {
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		mode: "onSubmit",
		defaultValues: {
			merchantOrderId: "",
			currency: "USD",
			price: undefined,
			cryptoCurrency: "",
			description: "",
			toleranceMode: "account",
			tolerancePercent: undefined,
		},
	});

	const toleranceMode = form.watch("toleranceMode");
	const tolerancePercent = form.watch("tolerancePercent");
	const price = form.watch("price");

	// Live preview of the USD tolerance that a positive percent would produce.
	// Matches the server-side formula for per-order overrides: priceUSD * percent / 100.
	const tolerancePreviewUsd = (() => {
		if (
			toleranceMode !== "shortfall" ||
			tolerancePercent === undefined ||
			!price
		) {
			return null;
		}
		return (price * tolerancePercent) / 100;
	})();

	const handleSubmit = (values: FormValues) => {
		const { toleranceMode: mode, tolerancePercent: percent, ...rest } = values;

		let paymentTolerancePercent: number | undefined;
		if (mode === "shortfall" && percent !== undefined) {
			paymentTolerancePercent = percent;
		} else if (mode === "exact") {
			paymentTolerancePercent = 0;
		}

		onFinish({
			...(rest as OrderParams),
			paymentTolerancePercent,
		});
	};

	const handleError = () => {
		onFinishFailed();
	};

	return (
		<div className="space-y-8">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="space-y-8"
				>
					<FormField
						control={form.control}
						name="merchantOrderId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Merchant Order ID</FormLabel>
								<FormControl>
									<Input placeholder="ORDER-12345" maxLength={255} {...field} />
								</FormControl>
								<FormDescription>This must be unique.</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid grid-cols-2 gap-4">
						<FormField
							control={form.control}
							name="price"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Price</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="99.99"
											step="0.01"
											min="0.01"
											{...field}
											onChange={(e) => {
												const value = e.target.value;
												field.onChange(
													value ? Number.parseFloat(value) : undefined,
												);
											}}
											value={field.value || ""}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="currency"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>Currency</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={CURRENCY[0]}
										value={field.value}
									>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select currency" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{CURRENCY.map((cur) => (
												<SelectItem key={cur} value={cur}>
													{cur}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="cryptoCurrency"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>Crypto Currency</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger className="w-full">
											{field.value ? (
												<CryptoIcon
													ticker={field.value}
													showLabel
													showComposite
												/>
											) : (
												<SelectValue placeholder="Select crypto currency" />
											)}
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{BLOCKCHAIN_TICKER.map((ticker) => (
											<SelectItem key={ticker} value={ticker}>
												<CryptoIcon ticker={ticker} showLabel showComposite />
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormDescription>
									Customer must use this exact cryptocurrency.
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Description (Optional)</FormLabel>
								<FormControl>
									<Textarea
										placeholder="Premium subscription for 12 months"
										rows={4}
										maxLength={500}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="space-y-4">
						<FormField
							control={form.control}
							name="toleranceMode"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Payment tolerance</FormLabel>
									<Select
										onValueChange={(val) => {
											field.onChange(val);
											if (val !== "shortfall") {
												form.setValue("tolerancePercent", undefined);
											}
										}}
										value={field.value}
									>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="account">
												Use my account default
											</SelectItem>
											<SelectItem value="shortfall">
												Accept a small shortfall
											</SelectItem>
											<SelectItem value="exact">
												Require exact payment
											</SelectItem>
										</SelectContent>
									</Select>
									<FormDescription>
										Choose how strictly we match the customer's payment against
										the expected amount for this order.
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						{toleranceMode === "shortfall" && (
							<FormField
								control={form.control}
								name="tolerancePercent"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Tolerance percent</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="0.5"
												step="0.01"
												min="0"
												max="100"
												{...field}
												onChange={(e) => {
													const value = e.target.value;
													field.onChange(
														value ? Number.parseFloat(value) : undefined,
													);
												}}
												value={field.value ?? ""}
											/>
										</FormControl>
										<FormDescription>
											{tolerancePreviewUsd !== null
												? `≈ ${formatPreviewUsd(tolerancePreviewUsd)} on this order.`
												: "Enter a percent (0.5 = 0.5%)."}
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
					</div>

					{/* Hidden submit button for form validation */}
					<Button type="submit" className="sr-only">
						Submit
					</Button>
				</form>
			</Form>
		</div>
	);
};

export default OrderCreateForm;
